import { DiagnosticsFooter, EmptyState, usePayload } from './primitives';
import styles from './styles.module.css';
import s from './MainInformationViewer.module.css';

const CONFIDENCE_CLASS = { High: s.confidenceHigh, Medium: s.confidenceMedium, Low: s.confidenceLow };

const MainInformationViewer = ({ report }) => {
  const payload = usePayload(report);
  if (!report) return <EmptyState />;

  const fields = Array.isArray(payload?.fields) ? payload.fields : [];
  const byKey = Object.fromEntries(fields.map((f) => [f.key, f]));

  const brandName        = byKey['brand_name']?.value || null;
  const domain           = byKey['primary_domain']?.value || null;
  const description      = byKey['brand_description']?.value || null;
  const country          = byKey['store_country']?.value || null;
  const email            = byKey['store_email']?.value || null;
  const platform         = byKey['commerce_platform']?.value || null;
  const businessModel    = byKey['business_model']?.value || null;
  const category         = byKey['normalized_category']?.value || null;
  const subcategory      = byKey['normalized_subcategory']?.value || null;
  const niche            = byKey['niche']?.value || null;
  const confidence       = byKey['classification_confidence']?.value || null;
  const notes            = byKey['classification_notes']?.value || null;
  const bizEvidence      = Array.isArray(byKey['business_model']?.evidence) ? byKey['business_model'].evidence : [];
  const nicheEvidence    = Array.isArray(byKey['niche']?.evidence) ? byKey['niche'].evidence : [];
  const allEvidence      = [...bizEvidence, ...nicheEvidence];

  const missing = Array.isArray(payload?.diagnostics?.missing_data_flags) ? payload.diagnostics.missing_data_flags : [];

  const metaItems = [
    country  && { label: 'Country',  value: country },
    email    && { label: 'Email',    value: email },
    platform && { label: 'Platform', value: platform },
  ].filter(Boolean);

  return (
    <div className={styles.viewer}>

      {/* Brand hero */}
      <div className={s.brandCard}>
        {brandName && <p className={s.brandName}>{brandName}</p>}
        {domain    && <p className={s.brandDomain}>{domain}</p>}
        {description && <p className={s.brandDescription}>{description}</p>}
      </div>

      {/* Store identity — compact meta pills */}
      {metaItems.length > 0 && (
        <div className={s.metaRow}>
          {metaItems.map((item, i) => (
            <div key={i} className={s.metaPill}>
              <span className={s.metaPillLabel}>{item.label}</span>
              <span className={s.metaDivider} />
              <span className={s.metaPillValue}>{item.value}</span>
            </div>
          ))}
        </div>
      )}

      {/* Classification block */}
      {(category || subcategory || niche) && (
        <div className={s.classificationBlock}>
          <div className={s.classificationHeader}>
            <span className={s.classificationTitle}>Classification</span>
            <div className={s.classificationBadges}>
              {businessModel && <span className={s.modelBadge}>{businessModel}</span>}
              {confidence && (
                <span className={`${s.confidenceBadge} ${CONFIDENCE_CLASS[confidence] || ''}`}>
                  {confidence}
                </span>
              )}
            </div>
          </div>

          <div className={s.breadcrumb}>
            {category && (
              <div className={s.breadcrumbItem}>
                <span className={s.breadcrumbLabel}>Category</span>
                <span className={s.breadcrumbValue}>{category}</span>
              </div>
            )}
            {category && subcategory && <span className={s.breadcrumbArrow}>›</span>}
            {subcategory && (
              <div className={s.breadcrumbItem}>
                <span className={s.breadcrumbLabel}>Subcategory</span>
                <span className={s.breadcrumbValue}>{subcategory}</span>
              </div>
            )}
            {subcategory && niche && <span className={s.breadcrumbArrow}>›</span>}
            {niche && (
              <div className={s.breadcrumbItem}>
                <span className={s.breadcrumbLabel}>Niche</span>
                <span className={s.breadcrumbValue}>{niche}</span>
              </div>
            )}
          </div>

          {allEvidence.length > 0 && (
            <div className={s.evidencePills}>
              {allEvidence.map((e, i) => (
                <span key={i} className={s.evidencePill}>{e}</span>
              ))}
            </div>
          )}

          {notes && <p className={s.classificationNote}>{notes}</p>}
        </div>
      )}

      <DiagnosticsFooter missingFlags={missing} />
    </div>
  );
};

export default MainInformationViewer;
